import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../api/apiService';
import '../styles/Auth.css';

const Register = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    address: '',
    preferredAlertMethod: 'email'
  });
  
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const nextStep = (e) => {
    e.preventDefault();
    console.log(`Moving from step ${step} to step ${step + 1}`);
    setStep(step + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    console.log('Submitting registration with data:', formData);
    
    try {
      // Call the registration API
      const result = await apiService.register(formData);
      console.log('Registration API response:', result);
      
      if (result.success) {
        console.log('Registration successful');
        
        // Auto login with the new credentials
        const loginResult = await login({
          email: formData.email,
          password: formData.password
        });
        
        if (loginResult.success) {
          console.log('Auto-login successful, navigating to dashboard');
          navigate('/dashboard');
        } else {
          console.log('Auto-login failed, navigating to login page');
          navigate('/login');
        }
      } else {
        console.error('Registration failed:', result.message);
        setError(result.message || 'Registration failed. Please try again.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-form-container">
        <h2>Create Your Account</h2>
        
        {error && <div className="auth-error">{error}</div>}
        
        <div className="registration-steps">
          <div className={`step ${step >= 1 ? 'active' : ''}`}>1</div>
          <div className="step-line"></div>
          <div className={`step ${step >= 2 ? 'active' : ''}`}>2</div>
          <div className="step-line"></div>
          <div className={`step ${step >= 3 ? 'active' : ''}`}>3</div>
        </div>
        
        {step === 1 && (
          <form className="auth-form" onSubmit={nextStep}>
            <h3>Account Information</h3>
            <div className="auth-form-row">
              <input 
                type="text" 
                name="firstName" 
                placeholder="First Name" 
                value={formData.firstName} 
                onChange={handleChange} 
                required 
              />
              <input 
                type="text" 
                name="lastName" 
                placeholder="Last Name" 
                value={formData.lastName} 
                onChange={handleChange} 
                required 
              />
            </div>
            <input 
              type="email" 
              name="email" 
              placeholder="Email" 
              value={formData.email} 
              onChange={handleChange} 
              required 
            />
            <input 
              type="password" 
              name="password" 
              placeholder="Password" 
              value={formData.password} 
              onChange={handleChange} 
              required 
            />
            <input 
              type="password" 
              name="confirmPassword" 
              placeholder="Confirm Password" 
              value={formData.confirmPassword} 
              onChange={handleChange} 
              required 
            />
            <button type="submit">Next</button>
            <div className="auth-link-row">
              <span>Already have an account?</span> <Link to="/login">Login</Link>
            </div>
          </form>
        )}
        
        {step === 2 && (
          <form className="auth-form" onSubmit={nextStep}>
            <h3>Contact Information</h3>
            <input 
              type="tel" 
              name="phoneNumber" 
              placeholder="Phone Number" 
              value={formData.phoneNumber} 
              onChange={handleChange} 
              required 
            />
            <input 
              type="text" 
              name="address" 
              placeholder="Address" 
              value={formData.address} 
              onChange={handleChange} 
              required 
            />
            <div className="form-group">
              <label>Preferred Alert Method:</label>
              <div className="radio-group">
                <div className="radio-option">
                  <input 
                    type="radio" 
                    name="preferredAlertMethod" 
                    value="email" 
                    id="email-radio" 
                    checked={formData.preferredAlertMethod === "email"} 
                    onChange={handleChange} 
                  />
                  <label htmlFor="email-radio">Email</label>
                </div>
                <div className="radio-option">
                  <input 
                    type="radio" 
                    name="preferredAlertMethod" 
                    value="sms" 
                    id="sms-radio" 
                    checked={formData.preferredAlertMethod === "sms"} 
                    onChange={handleChange} 
                  />
                  <label htmlFor="sms-radio">SMS</label>
                </div>
              </div>
            </div>
            <div className="auth-buttons">
              <button type="button" onClick={() => setStep(1)} className="back-button">Back</button>
              <button type="submit">Next</button>
            </div>
          </form>
        )}
        
        {step === 3 && (
          <form className="auth-form" onSubmit={handleSubmit}>
            <h3>Account Verification</h3>
            <p className="verification-message">
              A verification code has been sent to your email. Please check your inbox and enter the code below.
            </p>
            <div className="verification-code-input">
              <input type="text" maxLength="1" placeholder="•" />
              <input type="text" maxLength="1" placeholder="•" />
              <input type="text" maxLength="1" placeholder="•" />
              <input type="text" maxLength="1" placeholder="•" />
              <input type="text" maxLength="1" placeholder="•" />
              <input type="text" maxLength="1" placeholder="•" />
            </div>
            <p className="resend-link">
              Didn't receive code? <button type="button" className="text-button">Resend</button>
            </p>
            <div className="auth-buttons">
              <button type="button" onClick={() => setStep(2)} className="back-button">Back</button>
              <button type="submit" disabled={loading}>
                {loading ? 'Processing...' : 'Complete Registration'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Register; 